import { partition } from 'lodash-es'
import { useCallback } from 'react'

import { ConferenceError } from '../conference_error'
import { changePopupData, openPopup, removeLocation, selectLocation } from './actions'
import { INTEGRATIONS, KINDS_WITH_MANDATORY_ATTRIBUTES } from './constants'
import { LocationSelect } from './location_select'
import { NewLocationSelect } from './new_location_select'
import { SelectedLocation } from './selected_location'
import {
  availableLocationKindsSelector,
  createIsNewlyAddedLocationSelector,
  dividedAvailableLocationKindsSelector,
  enabledIntegrationsSelector,
  isDisabledSelector,
  isPendingSelector,
  showIntegrationErrorsSelector,
  useNewDropdownSelector,
  useNewLocationSelectSelector,
} from './selectors'
import { useDispatchValue, useStateValue } from './state_provider'

export const Location = ({
  locationId,
  location,
  isLocked,
  setIsSelectedForAllHosts = () => { },
  dropdownWrapper,
  hideErrors,
  isDisabled,
  ...rest
}) => {
  const state = useStateValue()
  const enabledIntegrations = enabledIntegrationsSelector(state)
  const dispatch = useDispatchValue()
  const showIntegrationErrors = showIntegrationErrorsSelector(state)
  const useNewDropdown = useNewDropdownSelector(state)
  const useNewLocationSelect = useNewLocationSelectSelector(state)
  const availableKinds = useNewDropdown && !useNewLocationSelect
    ? dividedAvailableLocationKindsSelector(state)
    : availableLocationKindsSelector(state)

  const isLockedOrDisabled = isLocked || isDisabledSelector(state)
  const isPending = isPendingSelector(state)
  const isNewlyAddedLocation = createIsNewlyAddedLocationSelector(locationId)(state)
  const hasIntegrationError = (
    INTEGRATIONS.includes(location.kind)
    && !enabledIntegrations.includes(location.kind)
  )

  const handleLocationEdit = useCallback(() => {
    setIsSelectedForAllHosts(false)
    dispatch(openPopup(locationId))
  }, [dispatch, locationId, setIsSelectedForAllHosts])

  const handleRemoveLocation = useCallback(() => {
    setIsSelectedForAllHosts(false)
    dispatch(removeLocation(locationId))
  }, [dispatch, locationId, setIsSelectedForAllHosts])

  const handleLocationSelect = useCallback((newKind) => {
    setIsSelectedForAllHosts(false)
    if (KINDS_WITH_MANDATORY_ATTRIBUTES.includes(newKind)) {
      dispatch(openPopup(locationId))
      dispatch(changePopupData({ kind: newKind }))
    } else {
      dispatch(selectLocation(locationId, newKind))
    }
  }, [dispatch, locationId, setIsSelectedForAllHosts])

  if (useNewLocationSelect) {
    const [webConferencingKinds, nonWebConferencingKinds] = partition(availableKinds, (kind) => kind.includes('conference'))
    return (
      <>
        <NewLocationSelect
          locationKind={location.kind}
          onChange={handleLocationSelect}
          onRemove={handleRemoveLocation}
          nonWebConferencingKinds={[...nonWebConferencingKinds, 'no_location']}
          webConferencingKinds={webConferencingKinds}
          dropdownWrapper={dropdownWrapper}
          isDisabled={isDisabled}
        />
        {!hideErrors && showIntegrationErrors && hasIntegrationError && (
          <ConferenceError
            noSpacing
            kind={location.kind}
            isDisabled={isLockedOrDisabled}
            hasPendingUser={isPending}
          />
        )}
      </>
    )
  }

  if (!location.kind) {
    return (
      <LocationSelect
        onChange={handleLocationSelect}
        availableKinds={availableKinds}
        isDisabled={isLockedOrDisabled}
        {...rest}
      />
    )
  }

  return (
    <>
      <SelectedLocation
        location={location}
        onEdit={handleLocationEdit}
        onClose={handleRemoveLocation}
        isInvalid={showIntegrationErrors && hasIntegrationError}
        isDisabled={isLockedOrDisabled}
        isNewlyAddedLocation={isNewlyAddedLocation}
      />
      {showIntegrationErrors && hasIntegrationError && (
        <ConferenceError
          kind={location.kind}
          isDisabled={isLockedOrDisabled}
          hasPendingUser={isPending}
        />
      )}
    </>
  )
}
